import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
  Autocomplete,
  Menu,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Grid
} from '@mui/material';
import { ListTodo, CheckCircle2, XCircle, Clock, Trash2, RefreshCw, FileText, Users, PhoneCall, Search, Mail } from 'lucide-react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useJob } from '../context/JobContext';
import api from '../services/api';

const StatusStatWidget = ({ title, value, subtitle, icon: Icon, color, active, onClick }) => (
  <Paper
    onClick={onClick}
    elevation={0}
    variant="outlined"
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '100%',
      cursor: 'pointer',
      borderWidth: active ? 2 : 1,
      borderStyle: 'solid',
      borderColor: active ? color : 'rgba(145, 158, 171, 0.24)',
      boxShadow: active
        ? '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)'
        : '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)',
      transition: 'box-shadow 0.2s, transform 0.1s, border-color 0.2s',
      '&:hover': {
        boxShadow:
          '0 0 2px 0 rgba(145, 158, 171, 0.3), 0 16px 32px -4px rgba(145, 158, 171, 0.28)',
        transform: 'translateY(-1px)'
      }
    }}
  >
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ml: 2
      }}
    >
      <Icon size={20} />
    </Box>
  </Paper>
);

const typeLabel = (type) => {
  if (type === 'cloudtalk_sync') return 'Sincronizzazione CloudTalk';
  if (type === 'open_banking_tx_sync') return 'Sincronizzazione Open Banking';
  if (type === 'passp_fatture_sync') return 'Sincronizzazione Fatture Passpartout';
  if (type === 'passp_clienti_sync') return 'Sincronizzazione Clienti Passpartout';
  if (type === 'passp_conti_sync') return 'Sincronizzazione Conti di Bilancio';
  if (type === 'passp_prima_nota_sync') return 'Sincronizzazione Prima Nota Passpartout';
  if (type === 'funnelix_import_sync') return 'Import contatti Funnelix';
  if (type === 'funnelix_verifica_clienti_csv') return 'Verifica clienti Funnelix (CSV)';
  if (type === 'open_banking_statement') return 'Estratto conto banche';
  if (type === 'customer_csv') return 'Esportazione CSV';
  if (type === 'cloudtalk_recall_csv') return 'Esportazione Recall Cloudtalk';
  if (type === 'dossier_recall_csv') return 'Esportazione Recall Preventivi';
  if (type === 'job_notturno') return 'Job Notturno (Scheduler)';
  if (type === 'passp_clienti_scheduler') return 'Scheduler Clienti Passpartout';
  if (type === 'passp_fatture_scheduler') return 'Scheduler Fatture Passpartout';
  if (type === 'passp_prima_nota_scheduler') return 'Scheduler Prima Nota Passpartout';
  if (type === 'scadenzario_import_csv') return 'Importazione piani CSV';
  if (type === 'scadenzario_performance_referenti_pdf') return 'Report Performance Referenti';
  if (type === 'kpi_lead_yearly_pdf') return 'Report KPI Lead Annuale';
  if (type === 'pf_clienti_assign') return 'Assegnazione clienti (Pratiche Finanziarie)';
  if (type === 'invii_modulistica_bulk_send_email') return 'Invio email promemoria';
  if (type === 'recessi_import_csv') return 'Import CSV Recessi';
  if (type === 'db_conn_cleanup') return 'Auto-cleanup connessioni DB';
  if (type === 'monitoring') return 'Notifica di monitoraggio';
  return 'Attività di sistema';
};

const typeIcon = (type) => {
  if (type === 'cloudtalk_sync') return <PhoneCall size={16} />;
  if (type === 'open_banking_tx_sync') return <RefreshCw size={16} />;
  if (type === 'passp_clienti_sync') return <Users size={16} />;
  if (type === 'funnelix_import_sync') return <Users size={16} />;
  if (type === 'funnelix_verifica_clienti_csv') return <Users size={16} />;
  if (type === 'job_notturno') return <Clock size={16} />;
  if (type === 'passp_clienti_scheduler' || type === 'passp_fatture_scheduler' || type === 'passp_prima_nota_scheduler') return <Clock size={16} />;
  if (type === 'db_conn_cleanup') return <Trash2 size={16} />;
  if (type === 'pf_clienti_assign') return <Users size={16} />;
  if (type === 'invii_modulistica_bulk_send_email') return <Mail size={16} />;
  return <FileText size={16} />;
};

const formatDuration = (ms) => {
  if (!ms || ms <= 0) return '-';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const restMin = minutes % 60;
  return restMin ? `${hours}h ${restMin}m` : `${hours}h`;
};

const entityLabel = (job) => {
  const { type, result, entityLabelFromLog } = job;
  if (entityLabelFromLog) {
    return entityLabelFromLog;
  }
  const r = result || {};

  if (type === 'open_banking_tx_sync') {
    if (r.accountName) {
      return `Account Open Banking: ${r.accountName}`;
    }
    if (typeof r.accounts === 'number' && r.accounts > 0) {
      return `Open Banking: ${r.accounts} account abilitati`;
    }
    return 'Open Banking: tutti gli account abilitati';
  }

  if (type === 'passp_fatture_sync') {
    const azienda = r.azienda || '';
    const from = r.dateFrom || r.date_from || null;
    const to = r.dateTo || r.date_to || null;

    const formatDate = (value) => {
      if (!value || typeof value !== 'string') return null;
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return value;
      const [, y, mo, d] = m;
      return `${d}/${mo}/${y}`;
    };

    const fromFmt = formatDate(from);
    const toFmt = formatDate(to);

    if (azienda && fromFmt && toFmt) {
      return `Fatture Passpartout – Azienda ${azienda} (${fromFmt} - ${toFmt})`;
    }
    if (azienda) {
      return `Fatture Passpartout – Azienda ${azienda}`;
    }
    return 'Fatture Passpartout';
  }

  if (type === 'passp_clienti_sync') {
    const azienda = r.azienda || '';
    const anno = r.anno || null;
    if (azienda && anno) {
      return `Clienti Passpartout – Azienda ${azienda}, anno ${anno}`;
    }
    if (azienda) {
      return `Clienti Passpartout – Azienda ${azienda}`;
    }
    return 'Clienti Passpartout';
  }

  if (type === 'passp_conti_sync') {
    const azienda = r.azienda || '';
    const anno = r.anno || null;
    if (azienda && anno) {
      return `Sincronizzazione Conti di Bilancio – Azienda ${azienda} / ${anno}`;
    }
    if (azienda) {
      return `Sincronizzazione Conti di Bilancio – Azienda ${azienda}`;
    }
    return 'Sincronizzazione Conti di Bilancio';
  }

  if (type === 'passp_prima_nota_sync') {
    const azienda = r.azienda || '';
    const from = r.dateFrom || r.date_from || null;
    const to = r.dateTo || r.date_to || null;

    const formatDate = (value) => {
      if (!value || typeof value !== 'string') return null;
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return value;
      const [, y, mo, d] = m;
      return `${d}/${mo}/${y}`;
    };

    const fromFmt = formatDate(from);
    const toFmt = formatDate(to);

    if (azienda && fromFmt && toFmt) {
      return `Prima Nota Passpartout – Azienda ${azienda} (${fromFmt} - ${toFmt})`;
    }
    if (azienda) {
      return `Prima Nota Passpartout – Azienda ${azienda}`;
    }
    return 'Prima Nota Passpartout';
  }

  if (type === 'cloudtalk_sync') {
    return 'CloudTalk – chiamate';
  }

  if (type === 'customer_csv') {
    return 'Esportazione clienti';
  }

  if (type === 'cloudtalk_recall_csv') {
    return 'Esportazione Recall Cloudtalk';
  }

  if (type === 'dossier_recall_csv') {
    return 'Esportazione Recall Preventivi';
  }

  if (type === 'funnelix_verifica_clienti_csv') {
    return 'Esportazione verifica clienti Funnelix';
  }

  if (type === 'monitoring') {
    return 'Monitoraggio interno';
  }

  return `Report ${type || ''}`.trim();
};

const messageText = (job) => {
  const msg =
    job.result && typeof job.result.message === 'string'
      ? job.result.message
      : job.message || '';
  if (!msg) return '-';
  return msg;
};

const formatMessageSummary = (message) => {
  if (!message || message === '-') return '-';
  const singleLine = message.replace(/\s*\n+\s*/g, ' • ');
  if (singleLine.length <= 160) return singleLine;
  return `${singleLine.slice(0, 157)}...`;
};

const statusChip = (status) => {
  if (status === 'active') {
    return <Chip label="In esecuzione" color="info" size="small" icon={<Clock size={14} />} />;
  }
  if (status === 'completed') {
    return <Chip label="Completato" color="success" size="small" icon={<CheckCircle2 size={14} />} />;
  }
  if (status === 'failed') {
    return <Chip label="Errore" color="error" size="small" icon={<XCircle size={14} />} />;
  }
  return <Chip label={status || 'Sconosciuto'} size="small" />;
};

export default function JobsMonitor() {
  const { jobs, removeJob } = useJob();

  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('24h');
  const [search, setSearch] = useState('');
  const [logRows, setLogRows] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [userFilter, setUserFilter] = useState(null); // { id, name } oppure null
  const [showFilters, setShowFilters] = useState(false);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({
    id: false
  });
  const [cleanupStatus, setCleanupStatus] = useState(null);

  const runningJobs = useMemo(
    () => jobs.filter((j) => j.status === 'active'),
    [jobs]
  );

  useEffect(() => {
    let alive = true;
    const fetchStatus = async () => {
      try {
        const res = await api.get('/job-logs/db-conn-cleanup-status');
        if (alive) setCleanupStatus(res.data || null);
      } catch {
        if (alive) setCleanupStatus(null);
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 30_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoadingLogs(true);
      try {
        const params = {};

        if (typeFilter !== 'all') params.type = typeFilter;
        if (statusFilter !== 'all') params.status = statusFilter;

        if (periodFilter !== 'all') {
          const now = new Date();
          const dayMs = 24 * 60 * 60 * 1000;
          let fromDate = null;
          if (periodFilter === '24h') {
            fromDate = new Date(now.getTime() - dayMs);
          } else if (periodFilter === '7d') {
            fromDate = new Date(now.getTime() - 7 * dayMs);
          }
          if (fromDate) {
            params.fromDate = fromDate.toISOString().slice(0, 19).replace('T', ' ');
          }
        }

        if (search.trim()) {
          params.search = search.trim();
        }

        if (userFilter) {
          if (userFilter.id !== undefined && userFilter.id !== null && userFilter.id !== '') {
            params.userId = userFilter.id;
          } else if (userFilter.name) {
            params.userName = userFilter.name;
          } else if (typeof userFilter === 'string' && userFilter) {
            params.userName = userFilter;
          }
        }

        params.limit = 300;

        const res = await api.get('/job-logs', { params });
        const payload = res.data || {};
        const rows = Array.isArray(payload.data) ? payload.data : [];

        const mapped = rows.map((row) => ({
          id: row.id, // id del log, unico
          jobId: row.jobId, // id del job nella coda
          type: row.type,
          status: row.status,
          createdAt: row.createdAt,
          completedAt: row.completedAt,
          durationMs: row.durationMs,
          result: row.message ? { message: row.message } : null,
          entityLabelFromLog: row.entityLabel || null,
          entityTypeFromLog: row.entityType || null,
          userNameFromLog: row.userName || null,
          _fromLog: true,
        }));

        setLogRows(mapped);
      } catch (error) {
        console.error('Error fetching job logs:', error);
      } finally {
        setLoadingLogs(false);
      }
    };

    fetchLogs();
  }, [typeFilter, statusFilter, periodFilter, search, userFilter]);

  const combinedJobs = useMemo(() => {
    // Per consistenza dei filtri (tipo, stato, periodo, utente) lavoriamo solo sui log storici.
    // I job "attivi" dal JobContext possono essere visualizzati altrove se necessario.
    return [...logRows];
  }, [logRows]);

  const runningJobsFiltered = useMemo(() => {
    if (!runningJobs.length) return [];
    const now = new Date();
    const searchLower = search.trim().toLowerCase();

    return runningJobs.filter((job) => {
      if (typeFilter !== 'all' && job.type !== typeFilter) {
        return false;
      }
      // Se filtro per stato specifico diverso da "all" o "active", non mostro i job attivi
      if (statusFilter !== 'all' && statusFilter !== 'active' && job.status !== statusFilter) {
        return false;
      }

      if (periodFilter !== 'all') {
        const created = job.createdAt ? new Date(job.createdAt) : null;
        if (!created || Number.isNaN(created.getTime())) {
          return false;
        }
        const diffMs = now.getTime() - created.getTime();
        const dayMs = 24 * 60 * 60 * 1000;
        if (periodFilter === '24h' && diffMs > dayMs) {
          return false;
        }
        if (periodFilter === '7d' && diffMs > 7 * dayMs) {
          return false;
        }
      }

      if (searchLower) {
        const idText = String(job.id || '');
        const typeText = typeLabel(job.type || '').toLowerCase();
        const msgText =
          job.result && typeof job.result.message === 'string'
            ? job.result.message.toLowerCase()
            : '';
        const entity = (job.entityLabel || '').toLowerCase();
        const haystack = `${idText} ${typeText} ${msgText} ${entity}`;
        if (!haystack.includes(searchLower)) {
          return false;
        }
      }

      if (userFilter && userFilter.id) {
        const jobUserId = job.userId;
        const jobUserName = job.userName;
        if (jobUserId && Number(jobUserId) === Number(userFilter.id)) {
          // ok
        } else if (jobUserName && jobUserName === userFilter.name) {
          // fallback per job attivi senza userId
        } else {
          return false;
        }
      }

      return true;
    });
  }, [runningJobs, typeFilter, statusFilter, periodFilter, search, userFilter]);

  const userOptions = useMemo(() => {
    const map = new Map();
    combinedJobs.forEach((job) => {
      const id = job.userIdFromLog || job.userId || null;
      const name = job.userNameFromLog || job.userName;
      if (!name || typeof name !== 'string') return;
      const key = id !== null && id !== undefined ? `id:${id}` : `name:${name}`;
      if (!map.has(key)) {
        map.set(key, { id, name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [combinedJobs]);

  const filteredJobs = useMemo(() => {
    // I log sono già filtrati dal backend per tipo, stato, periodo, utente e search.
    // Qui ci limitiamo a lavorare sull'array restituito, senza ulteriori filtri.
    return combinedJobs;
  }, [combinedJobs]);

  const sortedJobs = useMemo(() => {
    const stabilized = filteredJobs.map((job, index) => [job, index]);

    stabilized.sort((a, b) => {
      const jobA = a[0];
      const jobB = b[0];

      let valueA;
      let valueB;

      if (orderBy === 'type') {
        valueA = (jobA.type || '').toString().toLowerCase();
        valueB = (jobB.type || '').toString().toLowerCase();
      } else if (orderBy === 'status') {
        valueA = (jobA.status || '').toString().toLowerCase();
        valueB = (jobB.status || '').toString().toLowerCase();
      } else if (orderBy === 'durationMs') {
        valueA = Number(jobA.durationMs || 0);
        valueB = Number(jobB.durationMs || 0);
      } else if (orderBy === 'completedAt') {
        valueA = jobA.completedAt ? new Date(jobA.completedAt).getTime() : 0;
        valueB = jobB.completedAt ? new Date(jobB.completedAt).getTime() : 0;
      } else {
        // createdAt (default)
        valueA = jobA.createdAt ? new Date(jobA.createdAt).getTime() : 0;
        valueB = jobB.createdAt ? new Date(jobB.createdAt).getTime() : 0;
      }

      if (valueA < valueB) {
        return order === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === 'asc' ? 1 : -1;
      }
      return a[1] - b[1];
    });

    return stabilized.map((el) => el[0]);
  }, [filteredJobs, order, orderBy]);

  const paginatedJobs = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedJobs.slice(start, start + rowsPerPage);
  }, [sortedJobs, page, rowsPerPage]);

  const totalJobs = filteredJobs.length + runningJobsFiltered.length;
  const completedJobs = filteredJobs.filter((j) => j.status === 'completed').length;
  const activeJobs = runningJobsFiltered.length;
  const failedJobs = filteredJobs.filter((j) => j.status === 'failed').length;

  const completedWithDuration = filteredJobs.filter(
    (j) => j.status === 'completed' && typeof j.durationMs === 'number' && j.durationMs > 0
  );
  const avgDurationMs =
    completedWithDuration.length > 0
      ? completedWithDuration.reduce((sum, j) => sum + j.durationMs, 0) /
        completedWithDuration.length
      : null;

  const cleanupIndicator = useMemo(() => {
    const s = cleanupStatus;
    const formatIt = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return new Intl.DateTimeFormat('it-IT', {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }).format(d);
    };
    if (!s) {
      return {
        label: 'Auto-cleanup: -',
        color: 'default',
        tooltip:
          'Stato non disponibile.\n\nPossibili cause:\n- non sei loggato come Admin (endpoint protetto)\n- backend non raggiungibile\n- errore temporaneo di rete'
      };
    }
    const enabled = Boolean(s.enabled);
    const inWindow = Boolean(s.inWindow);
    const state = String(s.state || '');
    const lastOk = formatIt(s.lastSuccessAt);
    const lastFail = formatIt(s.lastFailAt);
    const minSeconds = Number(s.minSeconds || 0);

    if (!enabled) {
      return {
        label: 'Auto-cleanup: OFF',
        color: 'default',
        tooltip: `Disattivato (imposta DB_CONN_CLEANUP_ENABLED=1 nel .env root del docker-compose).\n\nFinestra: ${s?.window?.start || '08:30'}–${s?.window?.end || '19:00'} (${s?.tz || 'Europe/Rome'}).`
      };
    }
    if (!inWindow) {
      return {
        label: 'Auto-cleanup: Pausa',
        color: 'info',
        tooltip: `Attivo ma fuori fascia.\n\nFinestra: ${s?.window?.start || '08:30'}–${s?.window?.end || '19:00'} (${s?.tz || 'Europe/Rome'}).\nSoglia: > ${Math.round(minSeconds)}s.\nUltimo OK: ${lastOk || '-'}.`
      };
    }
    if (state === 'ok') {
      return {
        label: 'Auto-cleanup: OK',
        color: 'success',
        tooltip: `In esecuzione nella fascia.\n\nSoglia: > ${Math.round(minSeconds)}s.\nUltimo OK: ${lastOk || '-'}.`
      };
    }
    if (state === 'error') {
      return {
        label: 'Auto-cleanup: Errore',
        color: 'error',
        tooltip: `Errore recente.\n\nUltimo OK: ${lastOk || '-'}.\nUltimo errore: ${lastFail || '-'}.`
      };
    }
    if (state === 'stale') {
      return {
        label: 'Auto-cleanup: KO',
        color: 'warning',
        tooltip: `Nessun OK recente nella fascia.\n\nUltimo OK: ${lastOk || '-'}.\nUltimo errore: ${lastFail || '-'}.`
      };
    }
    return {
      label: 'Auto-cleanup: -',
      color: 'warning',
      tooltip: `Stato: ${state || 'n/d'}.\n\nUltimo OK: ${lastOk || '-'}.\nUltimo errore: ${lastFail || '-'}.`
    };
  }, [cleanupStatus]);

  useEffect(() => {
    setPage(0);
  }, [typeFilter, statusFilter, periodFilter, search, userFilter]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleToggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const handleOpenColumnMenu = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#4f46e5'
            }}
          >
            <ListTodo size={20} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Job di sistema
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Stato delle code di sistema: sincronizzazioni, report e notifiche tecniche.
            </Typography>
          </Box>
        </Box>
        <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{cleanupIndicator.tooltip}</span>}>
          <Chip
            size="small"
            color={cleanupIndicator.color}
            variant="outlined"
            label={cleanupIndicator.label}
            sx={{ borderRadius: '16px', fontWeight: 800 }}
          />
        </Tooltip>
      </Box>

      {runningJobsFiltered.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Job attualmente in esecuzione
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Tipo entità</TableCell>
                  <TableCell>Richiesto da</TableCell>
                  <TableCell>Stato</TableCell>
                  <TableCell>Creato il</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runningJobsFiltered.map((job) => (
                  <TableRow key={`running-${job.id}`}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {typeIcon(job.type)}
                        <Typography variant="body2">{typeLabel(job.type)}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.entityLabel || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.userName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusChip(job.status)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.createdAt
                          ? new Date(job.createdAt).toLocaleString()
                          : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Box>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatusStatWidget
            title="Totale job filtrati"
            value={totalJobs}
            icon={ListTodo}
            color="#0ea5e9"
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusStatWidget
            title="Completati"
            value={completedJobs}
            icon={CheckCircle2}
            color="#22c55e"
            active={statusFilter === 'completed'}
            onClick={() => setStatusFilter('completed')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusStatWidget
            title="In esecuzione"
            value={activeJobs}
            icon={Clock}
            color="#6366f1"
            active={statusFilter === 'active'}
            onClick={() => setStatusFilter('active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatusStatWidget
            title="In errore"
            value={failedJobs}
            subtitle={avgDurationMs ? `Durata media: ${formatDuration(avgDurationMs)}` : 'Durata media: n/d'}
            icon={XCircle}
            color="#ef4444"
            active={statusFilter === 'failed'}
            onClick={() => setStatusFilter('failed')}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          mb: 2,
          px: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            flexGrow: 1,
            minWidth: 0
          }}
        >
          <TextField
            size="small"
            variant="outlined"
            placeholder="Cerca per ID, tipo o messaggio..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 260, maxWidth: 360 }}
          />

          <Autocomplete
            size="small"
            options={userOptions}
            value={userFilter}
            onChange={(_event, newValue) => {
              setUserFilter(newValue || null);
            }}
            getOptionLabel={(option) => (option && option.name) || ''}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return option === value;
              if (option.id !== undefined && option.id !== null && value.id !== undefined && value.id !== null) {
                return Number(option.id) === Number(value.id);
              }
              return option.name === value.name;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Utente"
                placeholder="Filtra per utente"
              />
            )}
            sx={{ minWidth: 200, maxWidth: 260 }}
          />
        </Box>

        {userFilter && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              icon={<Users size={16} />}
              label={`Filtrato per utente: ${userFilter.name || userFilter.id}`}
              onDelete={() => setUserFilter(null)}
            />
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Tooltip title={showFilters ? 'Nascondi filtri avanzati' : 'Mostra filtri avanzati'}>
            <IconButton size="small" onClick={handleToggleFilters}>
              <FilterAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Colonne">
            <IconButton size="small" onClick={handleOpenColumnMenu}>
              <ViewColumnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showFilters && (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center'
          }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="job-type-filter-label">Tipo job</InputLabel>
            <Select
              labelId="job-type-filter-label"
              label="Tipo job"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="cloudtalk_sync">CloudTalk</MenuItem>
              <MenuItem value="open_banking_tx_sync">Open Banking</MenuItem>
              <MenuItem value="passp_fatture_sync">Fatture Passpartout</MenuItem>
              <MenuItem value="passp_clienti_sync">Clienti Passpartout</MenuItem>
              <MenuItem value="funnelix_import_sync">Import Funnelix</MenuItem>
              <MenuItem value="funnelix_verifica_clienti_csv">Verifica Clienti Funnelix (CSV)</MenuItem>
              <MenuItem value="customer_csv">Esportazioni CSV</MenuItem>
              <MenuItem value="dossier_recall_csv">CSV Recall Preventivi</MenuItem>
              <MenuItem value="cloudtalk_recall_csv">CSV Recall Cloudtalk</MenuItem>
              <MenuItem value="job_notturno">Job Notturno (Scheduler)</MenuItem>
              <MenuItem value="passp_clienti_scheduler">Scheduler Clienti Passpartout</MenuItem>
              <MenuItem value="passp_fatture_scheduler">Scheduler Fatture Passpartout</MenuItem>
              <MenuItem value="passp_prima_nota_scheduler">Scheduler Prima Nota Passpartout</MenuItem>
              <MenuItem value="report">Report generici</MenuItem>
              <MenuItem value="monitoring">Monitoraggio</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="job-status-filter-label">Stato</InputLabel>
            <Select
              labelId="job-status-filter-label"
              label="Stato"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="active">In esecuzione</MenuItem>
              <MenuItem value="completed">Completati</MenuItem>
              <MenuItem value="failed">Errore</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="job-period-filter-label">Periodo</InputLabel>
            <Select
              labelId="job-period-filter-label"
              label="Periodo"
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
            >
              <MenuItem value="24h">Ultime 24 ore</MenuItem>
              <MenuItem value="7d">Ultimi 7 giorni</MenuItem>
              <MenuItem value="all">Tutto</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      <Menu
        anchorEl={columnMenuAnchorEl}
        open={Boolean(columnMenuAnchorEl)}
        onClose={handleCloseColumnMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Colonne aggiuntive
          </Typography>
        </Box>
        <FormGroup sx={{ px: 2, pb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={columnVisibility.id}
                onChange={() => handleToggleColumn('id')}
              />
            }
            label="ID job"
          />
        </FormGroup>
      </Menu>

      <Card
        sx={{
          boxShadow:
            '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell sortDirection={orderBy === 'type' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? order : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell>Tipo entità</TableCell>
                <TableCell>Richiesto da</TableCell>
                <TableCell sortDirection={orderBy === 'status' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'durationMs' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'durationMs'}
                    direction={orderBy === 'durationMs' ? order : 'asc'}
                    onClick={() => handleRequestSort('durationMs')}
                  >
                    Durata
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.id && <TableCell>Dettagli</TableCell>}
                <TableCell>Messaggio</TableCell>
                <TableCell sortDirection={orderBy === 'createdAt' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'desc'}
                    onClick={() => handleRequestSort('createdAt')}
                  >
                    Creato il
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'completedAt' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'completedAt'}
                    direction={orderBy === 'completedAt' ? order : 'desc'}
                    onClick={() => handleRequestSort('completedAt')}
                  >
                    Completato il
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columnVisibility.id ? 10 : 9} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nessun job presente in questo momento.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedJobs.map((job) => (
                  <TableRow key={job._fromLog ? `log-${job.id}` : `job-${job.id}`} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {typeIcon(job.type)}
                        <Typography variant="body2">{typeLabel(job.type)}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {entityLabel(job)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {job.userNameFromLog || job.userName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusChip(job.status)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDuration(job.durationMs)}
                      </Typography>
                    </TableCell>
                    {columnVisibility.id && (
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          ID: {job.jobId || job.id}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {(() => {
                        const fullMessage = messageText(job);
                        const summary = formatMessageSummary(fullMessage);
                        const showTooltip = fullMessage && fullMessage !== '-' && fullMessage !== summary;
                        const content = (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 360,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {summary}
                          </Typography>
                        );
                        return showTooltip ? (
                          <Tooltip title={fullMessage} arrow placement="top">
                            <Box component="span">{content}</Box>
                          </Tooltip>
                        ) : (
                          content
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.createdAt
                          ? new Date(job.createdAt).toLocaleString('it-IT')
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {job.completedAt
                          ? new Date(job.completedAt).toLocaleString('it-IT')
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Rimuovi dalla lista">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (job._fromLog) {
                              setLogRows((prev) => prev.filter((j) => j.id !== job.id));
                            } else {
                              removeJob(job.jobId || job.id);
                            }
                          }}
                          color="default"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalJobs}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </Card>
    </Box>
  );
}
